import { CalendarDate, toZoned, ZonedDateTime } from "@internationalized/date"
import { createFileRoute } from "@tanstack/react-router"
import { InventoryTreeRateMiniAutoApplyPromotion } from "ipctg-inflow-types/InFlowAPIMisc"
import {
  DiscountDisplayMode,
  DiscountType,
  InventoryTreeRateMiniStatus,
  PromoFailReason,
} from "ipctg-inflow-types/InFlowEnums"
import { InventoryTreeRateMini } from "ipctg-inflow-types/InFlowReserveAPI"
import { GridList } from "react-aria-components"

import Card from "@/globalComponents/atoms/Card"
import Heading from "@/globalComponents/atoms/Heading"
import Text from "@/globalComponents/atoms/Text"
import ReservationRateCard from "@/routes/qr/$qrLookupID/reserve.$epoch/calendars/$calendarID/dates/$year.$month.$day/rates/-components/ReservationRateCard"
import { getLocale } from "@/utilities/etc"

export const Route = createFileRoute(
  "/qr/$qrLookupID/dev/organisms/reservation-rate-card"
)({
  staticData: {
    pageName: "ReservationRateCard Variants",
  },
  component: RouteComponent,
})

/*
  Fixed so the rendered copy never shifts with the clock — the reserve flow
  itself derives this from the route params and the theme timezone.
*/
const TIME_ZONE = "America/Denver"
const START_DATE = toZoned(new CalendarDate(2026, 6, 10), TIME_ZONE)

/** 5:00 AM June 10 → 12:00 PM June 11, i.e. the design's "31 hrs" pill. */
const RATE_START = START_DATE.set({ hour: 5 })
const RATE_END = RATE_START.add({ hours: 31 })

/** Finishes the day it starts, so the end time drops its date. */
const SAME_DAY_RATE_END = RATE_START.add({ hours: 9 })

/** Short same-day stay — the "2 hrs" pill. */
const SHORT_SAME_DAY_RATE_END = RATE_START.add({ hours: 2 })

/*
  Under an hour, so the duration pill falls back to minutes. Worth having
  alongside the hour-based cases because it exercises a different branch of
  the duration formatter.
*/
const SUB_HOUR_RATE_END = RATE_START.add({ minutes: 45 })

/** Ends at 11:59 PM the same day — the latest a same-day rate can run. */
const LATE_SAME_DAY_RATE_END = START_DATE.set({ hour: 23, minute: 59 })

const SAME_DAY = { endDateTime: SAME_DAY_RATE_END.toAbsoluteString() }

let mockRateID = 0

function makeRate(
  overrides: Partial<InventoryTreeRateMini> = {}
): InventoryTreeRateMini {
  mockRateID += 1
  const id = `mock-rate-${mockRateID}`
  return {
    rateStatus: InventoryTreeRateMiniStatus.Available,
    originalPrice: 35,
    finalPrice: 35,
    startDateTime: RATE_START.toAbsoluteString(),
    endDateTime: RATE_END.toAbsoluteString(),
    rateName: "Long-term parking",
    rateProductID: id,
    inventoryTreeRateID: id,
    warningMessage: "",
    membershipInfo: null,
    inventoryPoolID: id,
    inventoryTreeID: id,
    inventoryTreeRateAndCapacityID: id,
    bookingCalendarID: "mock-calendar",
    inventoryTreeRateMiniID: id,
    inventoryAccessPromotionID: null,
    inventoryAccessPromoCode: null,
    inventoryAccessMembershipID: null,
    membershipAccessCode: null,
    cancellationDeadline: null,
    discountDisplayMode: DiscountDisplayMode.none,
    discountedAmountDue: null,
    expectedAmountDue: null,
    expectedAmountDueBeforeDiscount: null,
    autoApplyPromotions: [],
    discountMembershipID: null,
    ...overrides,
  }
}

let mockPromotionID = 0

function makePromotion(
  userFacingRedeemCopy: string
): InventoryTreeRateMiniAutoApplyPromotion {
  mockPromotionID += 1
  return {
    promotionID: `mock-promotion-${mockPromotionID}`,
    promoCode: null,
    discountAmount: 10,
    discountType: DiscountType.Amount,
    userIsEligible: true,
    // Unread while userIsEligible is true
    notEligibleReason: PromoFailReason.InvalidCode,
    userFacingRedeemCopy,
  }
}

const MEMBERSHIP = { membershipName: "Season Pass Holder" }

const SHORT_PROMOTION = "Early bird weekend special"
/* Long enough to wrap onto a second line, short enough not to reach a third. */
const TWO_LINE_PROMOTION =
  "Early bird weekend special — save on off-peak parking"
const TWO_LINE_PROMOTION_ALT =
  "Weekday commuter discount applied automatically at checkout"
const LONG_PROMOTION =
  "Early bird weekend special — reserve before 6 AM Friday through Sunday and save on off-peak long-term parking at all downtown Inflow garages, limited-time summer rate"

/** Mirrors the Membership × Promotion × State matrix in Figma (CX App → Rates). */
const DISCOUNTED = {
  originalPrice: 35,
  finalPrice: 25,
  discountDisplayMode: DiscountDisplayMode.strikethroughPricing,
}

const ASTERISK = {
  originalPrice: 25,
  finalPrice: 20,
  discountDisplayMode: DiscountDisplayMode.nextStep,
}

/** Name says what the case is; traits say which matrix row it came from. */
interface Variant {
  name: string
  traits: Record<string, string>
  rate: InventoryTreeRateMini
}

interface VariantGroup {
  title: string
  description: string
  variants: Variant[]
}

const VARIANT_GROUPS: VariantGroup[] = [
  {
    title: "Membership applied",
    description:
      "membershipInfo is set, so the membership frame wraps the card.",
    variants: [
      {
        name: "Default",
        traits: { Promotion: "None", State: "Default" },
        rate: makeRate({ membershipInfo: MEMBERSHIP, finalPrice: 25 }),
      },
      {
        name: "Discounted",
        traits: { Promotion: "Short", State: "Discounted" },
        rate: makeRate({
          membershipInfo: MEMBERSHIP,
          ...DISCOUNTED,
          autoApplyPromotions: [makePromotion(SHORT_PROMOTION)],
        }),
      },
      {
        name: "Asterisk",
        traits: { Promotion: "Short", State: "Asterisk" },
        rate: makeRate({
          membershipInfo: MEMBERSHIP,
          ...ASTERISK,
          autoApplyPromotions: [makePromotion(SHORT_PROMOTION)],
        }),
      },
      {
        name: "Sold out",
        traits: { Promotion: "None", State: "Sold Out" },
        rate: makeRate({
          membershipInfo: MEMBERSHIP,
          rateStatus: InventoryTreeRateMiniStatus.SoldOut,
        }),
      },
      {
        name: "Limit reached",
        traits: { Promotion: "None", State: "Limit Reached" },
        rate: makeRate({
          membershipInfo: MEMBERSHIP,
          rateStatus: InventoryTreeRateMiniStatus.PrivilegeExhausted,
        }),
      },
    ],
  },
  {
    title: "No membership",
    description: "The card renders standalone, no frame.",
    variants: [
      {
        name: "Default",
        traits: { Promotion: "None", State: "Default" },
        rate: makeRate({}),
      },
      {
        name: "Discounted",
        traits: { Promotion: "Short", State: "Discounted" },
        rate: makeRate({
          ...DISCOUNTED,
          autoApplyPromotions: [makePromotion(SHORT_PROMOTION)],
        }),
      },
      {
        name: "Asterisk",
        traits: { Promotion: "Short", State: "Asterisk" },
        rate: makeRate({
          ...ASTERISK,
          autoApplyPromotions: [makePromotion(SHORT_PROMOTION)],
        }),
      },
      {
        name: "Long promotion copy",
        traits: { Promotion: "Long", State: "Discounted" },
        rate: makeRate({
          ...DISCOUNTED,
          autoApplyPromotions: [makePromotion(LONG_PROMOTION)],
        }),
      },
      {
        name: "Stacked promotions",
        traits: { Promotion: "Multiple", State: "Discounted" },
        rate: makeRate({
          ...DISCOUNTED,
          autoApplyPromotions: [
            makePromotion(SHORT_PROMOTION),
            makePromotion("Off-peak long-term parking"),
          ],
        }),
      },
      {
        name: "Sold out",
        traits: { Promotion: "None", State: "Sold Out" },
        rate: makeRate({ rateStatus: InventoryTreeRateMiniStatus.SoldOut }),
      },
    ],
  },
  {
    title: "Same day rates",
    description: "Ends the day it starts, so the end time drops its date.",
    variants: [
      {
        name: "Nine hours",
        traits: { Duration: "9 hrs", Promotion: "None", State: "Default" },
        rate: makeRate({ ...SAME_DAY }),
      },
      {
        name: "Two hours",
        traits: { Duration: "2 hrs", Promotion: "None", State: "Default" },
        rate: makeRate({
          endDateTime: SHORT_SAME_DAY_RATE_END.toAbsoluteString(),
          rateName: "Short stay parking",
          originalPrice: 12,
          finalPrice: 12,
        }),
      },
      {
        name: "Under an hour",
        traits: { Duration: "45 mins", Promotion: "None", State: "Default" },
        rate: makeRate({
          endDateTime: SUB_HOUR_RATE_END.toAbsoluteString(),
          rateName: "Quick stop",
          originalPrice: 6,
          finalPrice: 6,
        }),
      },
      {
        name: "Runs to midnight",
        traits: {
          Duration: "until 11:59 PM",
          Promotion: "None",
          State: "Default",
        },
        rate: makeRate({
          endDateTime: LATE_SAME_DAY_RATE_END.toAbsoluteString(),
          rateName: "All day parking",
        }),
      },
      {
        name: "Discounted",
        traits: { Duration: "9 hrs", Promotion: "Short", State: "Discounted" },
        rate: makeRate({
          ...SAME_DAY,
          ...DISCOUNTED,
          autoApplyPromotions: [makePromotion(SHORT_PROMOTION)],
        }),
      },
      {
        name: "Two-line promotion",
        traits: {
          Duration: "9 hrs",
          Promotion: "Two lines",
          State: "Discounted",
        },
        rate: makeRate({
          ...SAME_DAY,
          ...DISCOUNTED,
          autoApplyPromotions: [makePromotion(TWO_LINE_PROMOTION)],
        }),
      },
      {
        name: "Membership asterisk",
        traits: {
          Duration: "9 hrs",
          Membership: "Yes",
          Promotion: "Short",
          State: "Asterisk",
        },
        rate: makeRate({
          ...SAME_DAY,
          ...ASTERISK,
          membershipInfo: MEMBERSHIP,
          autoApplyPromotions: [makePromotion(SHORT_PROMOTION)],
        }),
      },
      {
        name: "Sold out",
        traits: { Duration: "9 hrs", Promotion: "None", State: "Sold Out" },
        rate: makeRate({
          ...SAME_DAY,
          rateStatus: InventoryTreeRateMiniStatus.SoldOut,
        }),
      },
    ],
  },
  {
    title: "Two-line promotions",
    description: "Redeem copy that wraps to two lines but stays compact.",
    variants: [
      {
        name: "Default",
        traits: { Promotion: "Two lines", State: "Default" },
        rate: makeRate({
          autoApplyPromotions: [makePromotion(TWO_LINE_PROMOTION)],
        }),
      },
      {
        name: "Discounted",
        traits: { Promotion: "Two lines", State: "Discounted" },
        rate: makeRate({
          ...DISCOUNTED,
          autoApplyPromotions: [makePromotion(TWO_LINE_PROMOTION)],
        }),
      },
      {
        name: "Asterisk",
        traits: { Promotion: "Two lines", State: "Asterisk" },
        rate: makeRate({
          ...ASTERISK,
          autoApplyPromotions: [makePromotion(TWO_LINE_PROMOTION)],
        }),
      },
      {
        name: "Membership",
        traits: {
          Membership: "Yes",
          Promotion: "Two lines",
          State: "Discounted",
        },
        rate: makeRate({
          ...DISCOUNTED,
          membershipInfo: MEMBERSHIP,
          autoApplyPromotions: [makePromotion(TWO_LINE_PROMOTION)],
        }),
      },
      {
        name: "Stacked two-line promotions",
        traits: { Promotion: "Two lines ×2", State: "Discounted" },
        rate: makeRate({
          ...DISCOUNTED,
          autoApplyPromotions: [
            makePromotion(TWO_LINE_PROMOTION),
            makePromotion(TWO_LINE_PROMOTION_ALT),
          ],
        }),
      },
      {
        name: "Long rate name",
        traits: {
          "Rate name": "Long",
          Promotion: "Two lines",
          State: "Default",
        },
        rate: makeRate({
          rateName: "Overnight long-term covered garage parking, level 3",
          autoApplyPromotions: [makePromotion(TWO_LINE_PROMOTION)],
        }),
      },
    ],
  },
  {
    title: "Cases outside the Figma matrix",
    description: "Not in the Figma matrix, but the API returns them.",
    variants: [
      {
        name: "Ended",
        traits: { Promotion: "None", State: "Ended" },
        rate: makeRate({ rateStatus: InventoryTreeRateMiniStatus.Ended }),
      },
      {
        name: "Free rate",
        traits: { Price: "0", State: "Default" },
        rate: makeRate({ originalPrice: 0, finalPrice: 0 }),
      },
      {
        name: "Ineligible promotion filtered out",
        traits: {
          "Rate name": "Long",
          Promotion: "Ineligible",
          State: "Default",
        },
        rate: makeRate({
          rateName: "Overnight long-term covered garage parking, level 3",
          autoApplyPromotions: [
            {
              ...makePromotion("You are not eligible for this"),
              userIsEligible: false,
              notEligibleReason: PromoFailReason.MembershipRequired,
            },
          ],
        }),
      },
    ],
  },
  {
    title: "Review fixes",
    description: "Each design-review fix beside its control.",
    variants: [
      {
        name: "End time drops the date",
        traits: { Duration: "Same day", Fix: "Yes" },
        rate: makeRate({ endDateTime: SAME_DAY_RATE_END.toAbsoluteString() }),
      },
      {
        name: "End time keeps the date",
        traits: { Duration: "Crosses midnight", Fix: "Control" },
        rate: makeRate({}),
      },
      {
        name: "Promotion hidden when sold out",
        traits: { Promotion: "Short", State: "Sold Out", Fix: "Yes" },
        rate: makeRate({
          rateStatus: InventoryTreeRateMiniStatus.SoldOut,
          autoApplyPromotions: [makePromotion(SHORT_PROMOTION)],
        }),
      },
      {
        name: "Promotion hidden when limit reached",
        traits: { Promotion: "Short", State: "Limit Reached", Fix: "Yes" },
        rate: makeRate({
          rateStatus: InventoryTreeRateMiniStatus.PrivilegeExhausted,
          autoApplyPromotions: [makePromotion(SHORT_PROMOTION)],
        }),
      },
      {
        name: "Promotion hidden when ended",
        traits: { Promotion: "Long", State: "Ended", Fix: "Yes" },
        rate: makeRate({
          rateStatus: InventoryTreeRateMiniStatus.Ended,
          autoApplyPromotions: [makePromotion(LONG_PROMOTION)],
        }),
      },
      {
        name: "Label, rule and copy align",
        traits: { Promotion: "Long", State: "Default", Fix: "Yes" },
        rate: makeRate({
          autoApplyPromotions: [makePromotion(LONG_PROMOTION)],
        }),
      },
      {
        name: "Bulleted list shares the column",
        traits: { Promotion: "Multiple", State: "Default", Fix: "Yes" },
        rate: makeRate({
          autoApplyPromotions: [
            makePromotion(SHORT_PROMOTION),
            makePromotion("Off-peak long-term parking"),
          ],
        }),
      },
      {
        name: "Compact promotion",
        traits: { Promotion: "Short", State: "Default", Fix: "Control" },
        rate: makeRate({
          autoApplyPromotions: [makePromotion(SHORT_PROMOTION)],
        }),
      },
    ],
  },
]

function slugify(title: string) {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, "-")
}

const STYLES = `
  .dev-container > *:not(:first-child) {
    margin-top: var(--space-5);
  }
  .variant-nav {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-1) var(--space-3);
    margin-top: var(--space-3);
    font-size: var(--font-size-1);
  }
  .variant-nav a {
    color: var(--neutral-11);
  }
  .variant-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: var(--space-6);
    margin-top: var(--space-5);
  }
  .variant-name {
    margin-bottom: var(--space-1);
  }
  .variant-traits {
    margin-bottom: var(--space-3);
    color: var(--neutral-11);
    font-size: var(--font-size-1);
  }
`

function RouteComponent() {
  return (
    <>
      <style>{STYLES}</style>
      <div className="dev-container">
        <Card>
          <Heading level={1} as={2}>
            ReservationRateCard
          </Heading>
          <Text>
            Every card variant in the Rates design, driven by mock{" "}
            <code>InventoryTreeRateMini</code> objects.
          </Text>
          <nav className="variant-nav">
            {VARIANT_GROUPS.map((group) => (
              <a key={group.title} href={`#${slugify(group.title)}`}>
                {group.title} ({group.variants.length})
              </a>
            ))}
          </nav>
        </Card>

        {VARIANT_GROUPS.map((group) => (
          <Card key={group.title} id={slugify(group.title)}>
            <Heading level={2} as={3}>
              {group.title}
            </Heading>
            <Text>{group.description}</Text>
            <div className="variant-grid">
              {group.variants.map((variant) => (
                <div key={variant.rate.inventoryTreeRateMiniID}>
                  <Text level={2} className="semi-bold variant-name">
                    {variant.name}
                  </Text>
                  <div className="variant-traits">
                    {Object.entries(variant.traits)
                      .map(([trait, value]) => `${trait} ${value}`)
                      .join(" · ")}
                  </div>
                  <VariantPreview rate={variant.rate} />
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </>
  )
}

function VariantPreview(props: { rate: InventoryTreeRateMini }) {
  return (
    <GridList
      aria-label={props.rate.rateName}
      style={{ display: "grid", gridTemplateColumns: "1fr" }}
    >
      <ReservationRateCard
        rate={props.rate}
        id={props.rate.inventoryTreeRateMiniID}
        textValue={props.rate.rateName}
        startDate={START_DATE as ZonedDateTime}
        locale={getLocale()}
        onOpenMembershipModal={() => {}}
      />
    </GridList>
  )
}
